import { useState, useCallback, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';
import { apiAddPhone } from '../api/auth';
import { ApiError } from '../api/client';

export default function AddPhonePage() {
  const user    = useAuthStore((s) => s.user);
  const setAuth = useAuthStore((s) => s.setAuth);
  const accessToken = useAuthStore((s) => s.accessToken);
  const navigate = useNavigate();

  const [phone, setPhone]       = useState('');
  const [error, setError]       = useState<string | null>(null);
  const [loading, setLoading]   = useState(false);

  // If phone already added, skip this step
  if (user?.phoneNumber) {
    navigate('/practice', { replace: true });
    return null;
  }

  const handleSubmit = useCallback(async (e: FormEvent) => {
    e.preventDefault();

    const trimmed = phone.trim();
    // Basic E.164 client-side check
    if (!/^\+[1-9]\d{6,14}$/.test(trimmed)) {
      setError('Enter a valid phone number in international format, e.g. +14155552671');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const { user: updated } = await apiAddPhone({ phoneNumber: trimmed });
      if (accessToken) setAuth(updated, accessToken);
      navigate('/practice', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('That phone number is already linked to another account');
      } else {
        setError('Failed to save phone number — please try again');
      }
    } finally {
      setLoading(false);
    }
  }, [phone, accessToken, setAuth, navigate]);

  return (
    <div className="auth-page">
      <div className="auth-header">
        <h1 className="auth-title">add phone number</h1>
        <p className="auth-subtitle">
          Used as a backup for account recovery. International format required.
        </p>
      </div>

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="form-field">
          <label className="form-label" htmlFor="phone">phone number</label>
          <input
            id="phone"
            name="phone"
            className={`form-input${error ? ' form-input--error' : ''}`}
            type="tel"
            autoComplete="tel"
            placeholder="+14155552671"
            value={phone}
            onChange={(e) => { setPhone(e.target.value); setError(null); }}
            disabled={loading}
            aria-describedby={error ? 'phone-error' : 'phone-hint'}
            aria-invalid={!!error}
          />
          {error ? (
            <span id="phone-error" className="form-error" role="alert">{error}</span>
          ) : (
            <span id="phone-hint" className="form-hint">
              Include country code, e.g. +1 for US
            </span>
          )}
        </div>

        <button
          className="btn btn--primary auth-submit-btn"
          type="submit"
          disabled={loading || !phone.trim()}
          aria-busy={loading}
        >
          {loading ? (
            <span className="auth-btn-loading">
              <span className="auth-spinner" />
              saving…
            </span>
          ) : 'save phone number'}
        </button>
      </form>

      <p className="auth-footer">
        <button
          className="btn--link"
          onClick={() => navigate('/practice', { replace: true })}
          type="button"
        >
          skip for now
        </button>
      </p>
    </div>
  );
}
